import React from "react";
import { money } from "@/lib/apiClient";
import { Loader2, Zap, ShieldCheck, Save, Mail, Copy } from "lucide-react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Switch } from "@/components/ui/switch";
import { Slider } from "@/components/ui/slider";

export const ApprovalRulesCard = ({ s, setS, onSave, saving }) => {
  const pct = Math.round((s.auto_approve_min_confidence || 0) * 100);
  return (
    <div className="rounded-xl border border-border bg-card p-6 space-y-6" data-testid="approval-rules-card">
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-primary/10 text-primary"><Zap className="h-5 w-5" /></span>
          <div>
            <div className="font-medium">Auto-approve safe invoices</div>
            <div className="text-sm text-muted-foreground">When on, invoices that are high-confidence, under your limit, and not duplicates are approved automatically.</div>
          </div>
        </div>
        <Switch data-testid="auto-approve-toggle" checked={s.auto_approve_enabled} onCheckedChange={(v) => setS({ ...s, auto_approve_enabled: v })} />
      </div>

      <div className="border-t border-border pt-6 space-y-6" style={{ opacity: s.auto_approve_enabled ? 1 : 0.5, pointerEvents: s.auto_approve_enabled ? "auto" : "none" }}>
        <div>
          <label className="text-sm font-medium flex items-center gap-2"><ShieldCheck className="h-4 w-4 text-emerald-600" /> Minimum confidence to auto-approve</label>
          <p className="text-xs text-muted-foreground mt-1 mb-3">Only invoices where every line meets this confidence are auto-approved.</p>
          <div className="flex items-center gap-4">
            <Slider data-testid="min-confidence-slider" value={[pct]} min={80} max={100} step={1} onValueChange={(v) => setS({ ...s, auto_approve_min_confidence: v[0] / 100 })} className="flex-1" />
            <span className="font-mono text-lg font-semibold w-16 text-right">{pct}%</span>
          </div>
        </div>
        <div>
          <label className="text-sm font-medium">Auto-approve amount limit</label>
          <p className="text-xs text-muted-foreground mt-1 mb-3">
            Invoices above this total always go to you for review — currently <span className="font-mono text-foreground">{money(s.auto_approve_max_amount, s.base_currency)}</span>{s.auto_approve_in_base ? " (base currency)" : ""}.
          </p>
          <div className="relative max-w-[220px]">
            <span className="absolute left-3 top-1/2 -translate-y-1/2 text-muted-foreground font-mono text-xs">{s.base_currency}</span>
            <Input data-testid="max-amount-input" type="number" min="0" step="50" value={s.auto_approve_max_amount} onChange={(e) => setS({ ...s, auto_approve_max_amount: e.target.value })} className="pl-12 font-mono" />
          </div>
        </div>
      </div>

      <div className="border-t border-border pt-5 flex items-start justify-between gap-4" data-testid="auto-reject-duplicates-row">
        <div className="flex items-start gap-3">
          <span className="flex h-10 w-10 shrink-0 items-center justify-center rounded-lg bg-amber-500/10 text-amber-600 dark:text-amber-400"><Copy className="h-5 w-5" /></span>
          <div>
            <div className="font-medium">Auto-reject exact duplicates</div>
            <div className="text-sm text-muted-foreground">An invoice with the <b>same supplier, invoice number and total</b> as one already in D20 is rejected on arrival and never sent to your accounting system. Near-matches (same number but a different total, or same amount and date) still go to a human.</div>
          </div>
        </div>
        <Switch data-testid="auto-reject-duplicates-toggle" checked={s.auto_reject_exact_duplicates !== false} onCheckedChange={(v) => setS({ ...s, auto_reject_exact_duplicates: v })} />
      </div>

      <div className="border-t border-border pt-5 flex justify-end">
        <Button data-testid="save-settings-btn" onClick={() => onSave()} disabled={saving}>
          {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Rules
        </Button>
      </div>
    </div>
  );
};

const EVENTS = [
  ["notify_received", "Received", "When an invoice is extracted into D20"],
  ["notify_approved", "Approved", "When a reviewer or rule approves it for payment"],
  ["notify_paid", "Paid", "When it's marked paid or included in a payment run"],
];

export const SupplierEmailsCard = ({ s, setS, onSave, saving }) => (
  <div className="rounded-xl border border-border bg-card p-6" data-testid="notification-settings">
    <div className="flex items-start gap-3 mb-4">
      <span className="flex h-10 w-10 items-center justify-center rounded-lg bg-rose-500/10 text-rose-500"><Mail className="h-5 w-5" /></span>
      <div>
        <div className="font-medium">Supplier status emails</div>
        <div className="text-sm text-muted-foreground">Sent as "D20 Invoice AI" to the supplier email on file (Suppliers page). Fixed templates, no reply-back requests.</div>
      </div>
    </div>
    <div className="divide-y divide-border">
      {EVENTS.map(([k, label, sub]) => (
        <div key={k} className="flex items-center justify-between py-3">
          <div><div className="text-sm font-medium">{label}</div><div className="text-xs text-muted-foreground">{sub}</div></div>
          <Switch data-testid={`${k.replace("_", "-")}-toggle`} checked={s[k]} onCheckedChange={(v) => setS({ ...s, [k]: v })} />
        </div>
      ))}
    </div>
    <div className="border-t border-border pt-4 flex justify-end">
      <Button data-testid="save-notify-btn" variant="outline" onClick={() => onSave("Email settings saved")} disabled={saving}>
        {saving ? <Loader2 className="h-4 w-4 mr-2 animate-spin" /> : <Save className="h-4 w-4 mr-2" />} Save Email Settings
      </Button>
    </div>
  </div>
);
